import { Button, Label, TableItem, TableMessage } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { Table } from 'components/organisms';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarPage } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import { useNewWindowNavigate } from 'hooks/useNewWindowNavigate';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  PiNavigationArrow,
  PiPencilSimpleLine,
  PiPlus,
  PiTrash,
} from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import {
  DELIVERY_METHOD,
  TRANSFER_METHOD,
} from 'services/digital-delivery/types';
import { environment } from 'services/environment/Environment';
import { IProviderForm, IProviderSalesChannel } from 'services/providers/types';
import { useGetSalesChannelsLightList } from 'services/salesChannel';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { II18nextPrefix } from 'types/components/components.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import {
  conditionalField,
  requiredEnumSelect,
} from 'utils/global/validation.utils';
import { array, mixed, object, string } from 'yup';
import { IFormikProps, IFormProps } from '../types';

export const providerSalesChannelInitialValues: IProviderSalesChannel = {
  salesChannelId: '',
  name: '',
  deliveryTypeId: DELIVERY_METHOD.unknown,
  transferMethodId: TRANSFER_METHOD.unknown,
  expectTimestamp: false,
  waitForAssetId: false,
};

export const providerInitialValues: IProviderForm = {
  name: '',
  salesChannels: [],
};

export const providerSalesChannelValidator = object({}).shape({
  salesChannelId: string().required(() => i18next.t('toast-error.required')),
  deliveryTypeId: requiredEnumSelect(DELIVERY_METHOD),
  transferMethodId: conditionalField(
    requiredEnumSelect(TRANSFER_METHOD),
    'deliveryTypeId',
    [DELIVERY_METHOD.Manual],
    mixed().notRequired()
  ),
});

export const providerValidator = object({}).shape({
  name: string().required(() => i18next.t('toast-error.required')),
  salesChannels: array().of(providerSalesChannelValidator),
});

export interface IProviderFormProps
  extends FormikConfig<IProviderForm>,
    IFormProps,
    II18nextPrefix {}

const ProviderForm = ({
  tittle,
  customBackItem,
  backPath,
  topbarContent,
  initialValues = providerInitialValues,
  status,
  ...props
}: IProviderFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navigateToNewWindow = useNewWindowNavigate();

  const salesChannelsLight = useGetSalesChannelsLightList();

  const { getSelectOptions, getStatusOptions } = useEnumToSelectOptions();

  const salesChannelOptions = (channels: string[]) =>
    salesChannelsLight.data
      ?.filter(channel => !channels.includes(channel.id))
      .map(channel => ({
        label: channel.name,
        value: channel.id,
      })) ?? [];

  const goToSalesChannel = (id: string) => {
    window.open(`/settings/sales-channel/${id}`, '_blank');
  };

  return (
    <Formik
      {...props}
      validationSchema={providerValidator}
      validateOnChange={true}
      validateOnBlur={true}
      initialStatus={status}
      enableReinitialize
      initialValues={initialValues}>
      {({ status, values, setFieldValue }: IFormikProps<IProviderForm>) => (
        <Form className='w-full h-full'>
          <TopbarPage
            topbar={
              <TopBar
                title={status?.displayMode ? tittle : undefined}
                customBackItem={customBackItem}
                path={backPath ?? 'settings/provider'}
                topbarContent={
                  <div className='flex w-full items-center justify-end'>
                    <div className='flex w-full'>
                      {!status?.displayMode && (
                        <FormInput.Text
                          name='name'
                          label={t('input.label.name')}
                          autoFocus
                        />
                      )}
                    </div>
                    <div className='flex w-max'>{topbarContent}</div>
                  </div>
                }
              />
            }>
            <Table
              // id={'ProviderForm-test'}
              layout={{
                resizedLayout: 'auto 17% 17% 200px 200px 80px',
              }}
              // displayMode
              data={{
                nodes: values.salesChannels.map((channel, key) => ({
                  id: key,
                  ...channel,
                })),
              }}
              columns={[
                {
                  label: t('input.label.salesChannel'),
                  renderCell: ({ name, salesChannelId, id }) => (
                    <TableItem>
                      {status?.displayMode ? (
                        <TableItem.Text text={name} />
                      ) : (
                        <FormInput.Select
                          name={`salesChannels[${id}].salesChannelId`}
                          label={t('input.label.salesChannel')}
                          options={salesChannelOptions(
                            values.salesChannels.map(
                              channel => channel.salesChannelId
                            )
                          )}
                          onChange={option => {
                            setFieldValue(
                              `salesChannels[${id}].salesChannelId`,
                              option.value
                            );
                            setFieldValue(
                              `salesChannels[${id}].name`,
                              option.label
                            );
                          }}
                          value={{ label: name ?? '-', value: salesChannelId }}
                        />
                      )}
                    </TableItem>
                  ),
                },
                {
                  label: t('input.label.deliveryMethod'),
                  renderCell: ({ name, deliveryTypeId, id }) => (
                    <TableItem>
                      {status?.displayMode ? (
                        <TableItem.Text
                          text={t(
                            `enum.DELIVERY_METHOD.${DELIVERY_METHOD[deliveryTypeId]}`
                          )}
                        />
                      ) : (
                        <FormInput.Select
                          name={`salesChannels[${id}].deliveryTypeId`}
                          label={t('input.label.deliveryMethod')}
                          options={getSelectOptions(
                            DELIVERY_METHOD,
                            'enum.DELIVERY_METHOD'
                          )}
                        />
                      )}
                    </TableItem>
                  ),
                },
                {
                  label: t('input.label.transferMethod'),
                  renderCell: ({
                    name,
                    transferMethodId,
                    deliveryTypeId,
                    salesChannelId,
                    id,
                  }) => (
                    <TableItem>
                      {deliveryTypeId !== DELIVERY_METHOD.Manual ? (
                        status?.displayMode ? (
                          environment().node.nodeEnv === 'development' ? (
                            <Button
                              isClear
                              onlyIcon
                              onClick={() =>
                                navigate(
                                  `${TRANSFER_METHOD[
                                    transferMethodId
                                  ].toLocaleLowerCase()}-connection/${salesChannelId}/keys`
                                )
                              }>
                              <Button.Text
                                text={t(
                                  `enum.TRANSFER_METHOD.${TRANSFER_METHOD[transferMethodId]}`
                                )}
                              />
                                                            <Button.Description description={t('button.edit')} />

                                                            <Button.Icon icon={<PiPencilSimpleLine />} />
                            </Button>
                          ) : (
                            t(
                              `enum.TRANSFER_METHOD.${TRANSFER_METHOD[transferMethodId]}`
                            )
                          )
                        ) : (
                          <FormInput.Select
                            name={`salesChannels[${id}].transferMethodId`}
                            label={t('input.label.transferMethod')}
                            options={getSelectOptions(
                              TRANSFER_METHOD,
                              'enum.TRANSFER_METHOD'
                            )}
                          />
                        )
                      ) : (
                        '-'
                      )}
                    </TableItem>
                  ),
                },
                {
                  label: t('input.label.waitForAssetId'),
                  renderCell: ({
                    name,
                    waitForAssetId,
                    deliveryTypeId,
                    id,
                  }) => (
                    <TableItem>
                      {deliveryTypeId !== DELIVERY_METHOD.Manual ? (
                        status?.displayMode ? (
                          waitForAssetId && (
                            <Label color='blue' size='compact' withText>
                              {t('input.label.waitForAssetId')}
                            </Label>
                          )
                        ) : (
                          <FormInput.Checkbox
                            name={`salesChannels[${id}].waitForAssetId`}
                          />
                        )
                      ) : (
                        '-'
                      )}
                    </TableItem>
                  ),
                },
                {
                  label: t('input.label.expectTimestamp'),
                  renderCell: ({
                    name,
                    expectTimestamp,
                    deliveryTypeId,
                    id,
                  }) => (
                    <TableItem>
                      {deliveryTypeId !== DELIVERY_METHOD.Manual ? (
                        status?.displayMode ? (
                          expectTimestamp && (
                            <Label size='compact' withText>
                              {t('input.label.expectTimestamp')}
                            </Label>
                          )
                        ) : (
                          <FormInput.Checkbox
                            name={`salesChannels[${id}].expectTimestamp`}
                          />
                        )
                      ) : (
                        '-'
                      )}
                    </TableItem>
                  ),
                },
                {
                  label: '',
                  renderCell: ({ salesChannelId }) => (
                    <TableItem justifyPosition='end'>
                      {status?.displayMode ? (
                        <Button
                          isClear
                          onlyIcon
                          onClick={() => goToSalesChannel(salesChannelId)}>
                                                    <Button.Description description={t('button.open')} />

                                                    <Button.Icon
                            icon={<PiNavigationArrow transform='scale(-1,1)' />}
                          />
                        </Button>
                      ) : (
                        <Button
                          variant={BUTTON_VARIANT.remove}
                          isClear
                          onlyIcon
                          onClick={() =>
                            setFieldValue(
                              `salesChannels`,
                              values.salesChannels.filter(
                                channel =>
                                  channel.salesChannelId !== salesChannelId
                              )
                            )
                          }>
                                                    <Button.Description description={t('button.remove')} />

                                                    <Button.Icon icon={<PiTrash />} />
                        </Button>
                      )}
                    </TableItem>
                  ),
                },
              ]}
              tableOptions={
                status?.displayMode
                  ? undefined
                  : {
                      renderAfterTable: () => (
                        <tfoot className='full-width-footer'>
                          <TableMessage
                            className='cursor-pointer'
                            disable={
                              values.salesChannels.length
                                ? !values.salesChannels[
                                    values.salesChannels.length - 1
                                  ]?.salesChannelId.length
                                : false
                            }
                            onClick={() =>
                              setFieldValue(
                                `salesChannels[${values.salesChannels.length}]`,
                                providerSalesChannelInitialValues
                              )
                            }>
                            <TableItem.Text text={t('button.add')} />
                            <PiPlus />
                          </TableMessage>
                        </tfoot>
                      ),
                    }
              }
              size='compact'
            />
          </TopbarPage>
        </Form>
      )}
    </Formik>
  );
};

export default ProviderForm;
